import { useState } from 'react';

import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { Address } from 'types/graphql';

import { useMutation } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { InputWithSearch } from 'src/components/InputWithSearch';
import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { AddressListModal } from 'src/components/Modals';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { ADDRESS_LOOK_UP } from 'src/graphql/mutations/addressLookUp.mutation';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

export type SingleAddressBlockProps = {
    label: string;
    formKeys: {
        addressLine1: string;
        addressLine2: string;
        townOrCity: string;
        county: string;
        country: string;
        eircode: string;
    };
    setFieldValue: (field: string, value: any) => void;
};

export type OpenModalState = { open: boolean };
export type AddressList = { id: string; text: string };

const SingleAddressBlock = ({ label, formKeys, setFieldValue }: SingleAddressBlockProps) => {
    const { merchantApplication } = useAddMerchantContext();
    const { getPostalLabel } = useCountryHelpers();
    const [openModal, setOpenModal] = useState<OpenModalState>({ open: false });
    const [addressList, setAddressList] = useState<AddressList[]>([]);
    const [formattedAddress, setFormattedAddress] = useState<string | null>(null);

    const isEditingDisabled = isApplicationEditable(merchantApplication);
    const postalLabel = getPostalLabel(merchantApplication?.partner?.countryDetails?.alpha2Code);

    const [addressLookUp, { loading }] = useMutation(ADDRESS_LOOK_UP, {
        onError: () => {
            return showErrorToast('Error Searching for Address');
        },
        onCompleted: ({ addressLookUp }) => {
            setAddressList(addressLookUp);
            setOpenModal({ open: true });
        },
    });

    const onSearch = async (value: string) => {
        await addressLookUp({ variables: { address: value } });
    };

    const onSelectedAddress = (address: Address, formatted: string) => {
        setFieldValue(formKeys.addressLine1, address.line1);
        setFieldValue(formKeys.addressLine2, address.line2);
        setFieldValue(formKeys.townOrCity, address.city);
        setFieldValue(formKeys.county, address.province);
        setFieldValue(formKeys.country, address.countryName);
        setFieldValue(formKeys.eircode, address.postalCode);
        setFormattedAddress(formatted);
    };

    return (
        <>
            <MainCard>
                {loading && <Loader />}

                {/* render as div instead of default <p> */}
                <Typography component="div" fontSize="24px" color="#1D1D1B" marginBottom="8px">
                    {label}
                </Typography>

                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Box className="mt-20">
                    <Stack spacing={4} sx={{ marginBottom: '40px' }}>
                        {/* Row 1 - Address Search */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item width="100%">
                                    <Grid item xs={12} md={12}>
                                        <InputWithSearch
                                            searchText="Search"
                                            onSearch={onSearch}
                                            placeholder="Search"
                                            label="Search"
                                            fieldName="singleAddressSearch"
                                            maskedValue={formattedAddress}
                                            clearMaskedValue={() => {
                                                return setFormattedAddress(null);
                                            }}
                                            disabled={isEditingDisabled}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Row 2 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 1"
                                        name={formKeys.addressLine1}
                                        placeholder="Address Line 1"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Address Line 2"
                                        name={formKeys.addressLine2}
                                        placeholder="Address Line 2"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Row 3 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="Town/City"
                                        name={formKeys.townOrCity}
                                        placeholder="Enter Town/City"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label="County"
                                        name={formKeys.county}
                                        placeholder="Enter County"
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Row 4 */}
                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <CountrySelectInput
                                        setFieldValue={setFieldValue}
                                        fieldName={formKeys.country}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputBaseField
                                        label={postalLabel}
                                        name={formKeys.eircode}
                                        placeholder={postalLabel}
                                        disabled={isEditingDisabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Box>
            </MainCard>

            <AddressListModal
                isOpen={openModal.open}
                onClose={() => {
                    return setOpenModal({ open: false });
                }}
                addressList={addressList}
                onSelectedAddress={(address, formatted) => {
                    onSelectedAddress(address, formatted);
                    setOpenModal({ open: false });
                }}
            />
        </>
    );
};

export default SingleAddressBlock;
